import { ColorFamilyList, ColorFamilyName, filterOnlyColorFamilyName } from '@/models/tag/color-family'
import { uniq } from 'lodash'

export const maxCheckNumber = 10

export class ColorFamilyState {
  static empty(): ColorFamilyState {
    return new ColorFamilyState(ColorFamilyList.empty(), CheckedColorFamilyNameList.empty())
  }

  static initialize(
    list: ColorFamilyList,
    checked: string[]
  ): ColorFamilyState {
    return new ColorFamilyState(
      list,
      new CheckedColorFamilyNameList(checked)
    )
  }

  private constructor(
    public readonly list: ColorFamilyList,
    private checked: CheckedColorFamilyNameList
  ) {}

  get checkedList(): ColorFamilyList {
    return this.list.filter(c => this.checked.some(name => c.name === name))
  }

  get alreadyCheckedMaxNumber(): boolean {
    return this.checked.alreadyCheckedMaxNumber
  }

  tryToToggleCheck(
    name: ColorFamilyName,
    fallback: (message: string) => void = (_message: string) => { /* nothing */ }
  ): ColorFamilyState {
    if (this.isChecked(name)) {
      return this.uncheck(name)
    }

    try {
      return this.tryToCheck(name)
    } catch (e) {
      fallback(e.message)
    }

    return this
  }

  isChecked(name: ColorFamilyName): boolean {
    return this.checked.some(n => n === name)
  }

  uncheckAll(): ColorFamilyState {
    return new ColorFamilyState(this.list, CheckedColorFamilyNameList.empty())
  }

  toQuery(): string {
    return this.checkedList.map(c => c.name).join(',')
  }

  private tryToCheck(name: ColorFamilyName): ColorFamilyState {
    return new ColorFamilyState(
      this.list,
      this.checked.tryToAdd(name)
    )
  }

  private uncheck(name: ColorFamilyName): ColorFamilyState {
    return new ColorFamilyState(
      this.list,
      this.checked.remove(name)
    )
  }
}

/**
 * @internal ColorFamilyStateが内部的に利用することを想定している
 */
export class CheckedColorFamilyNameList {
  static empty(): CheckedColorFamilyNameList {
    return new CheckedColorFamilyNameList([])
  }

  private list: ColorFamilyName[]

  /**
   * - リストには正しいカラー系統名のみ保持される
   * - リストではカラー系統名の重複は排除される
   * - リストは選択上限以内であることを保証する
   */
  constructor(maybeColorNameList: string[]) {
    const listOnlyValidColorFamilyName = filterOnlyColorFamilyName(maybeColorNameList)
    const uniqListOnlyValidColorFamilyName = uniq(listOnlyValidColorFamilyName)
    this.list = uniqListOnlyValidColorFamilyName.splice(0, maxCheckNumber)
  }

  get alreadyCheckedMaxNumber(): boolean {
    return this.list.length >= maxCheckNumber
  }

  get value(): ColorFamilyName[] {
    return this.list
  }

  some(predicate: (name: ColorFamilyName) => boolean): boolean {
    return this.list.some(predicate)
  }

  filter(predicate: (name: ColorFamilyName) => boolean): CheckedColorFamilyNameList {
    return new CheckedColorFamilyNameList(this.list.filter(predicate))
  }

  /**
   * @throws Error 選択上限数を超過する場合
   */
  tryToAdd(name: ColorFamilyName): CheckedColorFamilyNameList {
    if (this.alreadyCheckedMaxNumber) {
      throw new Error(`選択できるカラーの数は最大で${maxCheckNumber}個です。`)
    }

    return new CheckedColorFamilyNameList([...this.list, name])
  }

  remove(name: ColorFamilyName): CheckedColorFamilyNameList {
    return this.filter(n => n !== name)
  }
}
